import { useReducer, useMemo, useEffect } from 'react';
import { ResizeDirection } from '@decisions/server/DecisionsFramework/Design/Form';
import { IPoint, IRect } from 'utils/GeometryUtils';
import { useDidUpdateEffect } from './useDidUpdateEffect';
import { isLeftButtonPressed } from 'utils/KeyBoardUtils';
import { isNumber } from 'utils/NumberUtils';

export interface IBounds {
  left?: number;
  top?: number;
}

export type IResizeAction =
  | {
      type: 'start';
      position: IPoint;
      resizeDirection: ResizeDirection;
      initialRect: IRect;
    }
  | {
      type: 'move' | 'stop';
      position: IPoint;
      bounds?: IBounds;
    };

export interface IResizeState {
  isActive: boolean;
  initialCursorPoint: IPoint;
  currentCursorPoint: IPoint;
  initialRect: IRect;
  currentRect: IRect;
  direction: ResizeDirection | null;
}

export interface IResizeEvent {
  cursor: IPoint;
}

export interface IStartResizeEvent extends IResizeEvent {
  direction: ResizeDirection;
  initialRect: IRect;
}

export interface IUseResizeResult extends Omit<IResizeState, 'initialCursorPoint' | 'currentCursorPoint'> {
  start: (event: IStartResizeEvent) => void;
  stop: (event: IResizeEvent) => void;
  move: (event: IResizeEvent) => void;
}

export interface IUseResizeProps {
  /**
   * Determines whether need to log resize events like 'start', 'move', 'stop'
   * @default false
   */
  logEvents?: boolean;
  bounds?: IBounds;
  onResizeStart?: (result: IUseResizeResult) => void;
  onResizing?: (result: IUseResizeResult) => void;
  onResizeEnd?: (result: IUseResizeResult) => void;
  // minWidth: number;
  // minHeight: number;
}

/**
 * Calculates result size and position based on current cursor position @param position.
 *
 * Note: exported only for testing in useResize.test.ts
 *
 * @param state Initial state which object had before start of resizing
 * @param position Current cursor position
 */
export function getSizeAndPosition(
  { direction, initialRect, initialCursorPoint }: IResizeState,
  position: IPoint,
  bounds?: IBounds
): IRect {
  if (direction == null) {
    const defaultSizeAndPosition = { width: 0, height: 0, left: 0, top: 0 };
    return defaultSizeAndPosition;
  }

  const newRect = { ...initialRect };
  let deltaX = position.x - initialCursorPoint.x;
  let deltaY = position.y - initialCursorPoint.y;

  if (direction & ResizeDirection.Left) {
    if (isNumber(bounds?.left)) {
      const minDeltaX = bounds!.left! - initialRect.left;
      if (deltaX < minDeltaX) {
        deltaX = minDeltaX;
      }
    }

    let computedWidth = Math.max(newRect.width - deltaX, 10);
    if (computedWidth > 10) {
      newRect.width = computedWidth;
      newRect.left += deltaX;
    }
  }

  if (direction & ResizeDirection.Top) {
    if (isNumber(bounds?.top)) {
      const minDeltaY = bounds!.top! - initialRect.top;
      if (deltaY < minDeltaY) {
        deltaY = minDeltaY;
      }
    }

    let computedHeight = Math.max(newRect.height - deltaY, 10);
    if (computedHeight > 10) {
      newRect.height = computedHeight;
      newRect.top += deltaY;
    }
  }
  if (direction & ResizeDirection.Right) {
    newRect.width = Math.max(newRect.width + deltaX, 10);
  }
  if (direction & ResizeDirection.Bottom) {
    newRect.height = Math.max(newRect.height + deltaY, 10);
  }

  return newRect;
}

/**
 * Creates resize even from any mouse event.
 * It will prevent copypasting this logic.
 */
export function createResizeEvent<T extends IResizeEvent>(
  e: React.MouseEvent<HTMLElement> | MouseEvent,
  otherProps?: Omit<T, 'cursor'>
): T {
  return {
    ...(otherProps ?? {}),
    cursor: {
      x: e.pageX,
      y: e.pageY,
    },
  } as T;
}

const resizeReducer: React.Reducer<IResizeState, IResizeAction> = (state, action) => {
  switch (action.type) {
    case 'start': {
      return {
        isActive: true,
        direction: action.resizeDirection,
        initialCursorPoint: action.position,
        currentCursorPoint: action.position,
        initialRect: action.initialRect,
        currentRect: action.initialRect,
      };
    }
    case 'move': {
      if (!state.isActive) {
        return state;
      }

      return {
        ...state,
        currentCursorPoint: action.position,
        currentRect: getSizeAndPosition(state, action.position, action.bounds),
      };
    }
    case 'stop': {
      if (!state.isActive) {
        return state;
      }

      return {
        ...state,
        isActive: false,
        currentCursorPoint: action.position,
        currentRect: getSizeAndPosition(state, action.position, action.bounds),
        direction: null,
      };
    }
  }
};

export function useResize(props?: IUseResizeProps): IUseResizeResult {
  const [{ isActive, initialRect, currentRect, direction }, dispatch] = useReducer(resizeReducer, {
    isActive: false,
    initialCursorPoint: { x: 0, y: 0 },
    currentCursorPoint: { x: 0, y: 0 },
    direction: null,
    currentRect: { height: 0, width: 0, left: 0, top: 0 },
    initialRect: { height: 0, width: 0, left: 0, top: 0 },
  });

  const result = useMemo(
    () => ({
      isActive,
      direction,
      initialRect,
      currentRect,
      start: (e: IStartResizeEvent) => {
        const action: IResizeAction = {
          type: 'start',
          position: e.cursor,
          resizeDirection: e.direction,
          initialRect: e.initialRect,
        };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
      move: (e: IResizeEvent) => {
        if (!isActive) {
          return;
        }

        const action: IResizeAction = { type: 'move', position: e.cursor, bounds: props?.bounds };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
      stop: (e: IResizeEvent) => {
        if (!isActive) {
          return;
        }

        const action: IResizeAction = { type: 'stop', position: e.cursor, bounds: props?.bounds };
        props && props.logEvents && console.log(action);
        dispatch(action);
      },
    }),
    [currentRect, direction, initialRect, isActive, props]
  );

  useDidUpdateEffect(() => {
    if (isActive) {
      props?.onResizeStart?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, props]);

  useDidUpdateEffect(() => {
    if (isActive) {
      props?.onResizing?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, props]);

  useDidUpdateEffect(() => {
    if (!isActive) {
      props?.onResizeEnd?.(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    function stop(event: MouseEvent) {
      result.stop(createResizeEvent(event));
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
    }

    const mouseMove = (event: MouseEvent) => {
      // sometimes for some reasone, button can be unpressed during "mousemove", but "moseup" event doesn't happen
      // so we check whether button is pressed and call "stop" if it doesn't.
      if (isLeftButtonPressed(event)) {
        result.move(createResizeEvent(event));
      } else {
        stop(event);
      }
    };

    const mouseUp = (event: MouseEvent) => {
      stop(event);
    };

    document.addEventListener('mousemove', mouseMove);
    document.addEventListener('mouseup', mouseUp);

    return () => {
      document.removeEventListener('mousemove', mouseMove);
      document.removeEventListener('mouseup', mouseUp);
    };
  }, [result]);

  return result;
}
